import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";

function Chart({ data, height = 200 }) {
  if (!data?.length) return <div className="text-white/50 text-sm py-10 text-center">No visits yet in this window.</div>;
  const w = 900, pad = 30;
  const max = Math.max(1, ...data.map((d) => d.visits));
  const stepX = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
  const pts = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = height - pad - ((d.visits / max) * (height - pad * 2));
    return { x, y, d };
  });
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const area = `${path} L ${pts[pts.length - 1].x.toFixed(1)} ${height - pad} L ${pts[0].x.toFixed(1)} ${height - pad} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full h-auto" preserveAspectRatio="none">
      {/* grid */}
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={pad} x2={w - pad} y1={pad + (height - pad * 2) * f} y2={pad + (height - pad * 2) * f} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      ))}
      {/* area */}
      <motion.path
        d={area}
        fill="url(#chart-fill)"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
      />
      {/* line */}
      <motion.path
        d={path}
        fill="none" stroke="#C5A059" strokeWidth="1.5"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
      />
      {/* dots */}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={2.5} fill="#C5A059" />
          <title>{`${p.d.date}: ${p.d.visits} visits, ${p.d.sessions} sessions`}</title>
        </g>
      ))}
      {/* labels first / last */}
      <text x={pad} y={height - 5} fill="rgba(255,255,255,0.4)" fontSize="10" fontFamily="Manrope">{data[0]?.date}</text>
      <text x={w - pad} y={height - 5} textAnchor="end" fill="rgba(255,255,255,0.4)" fontSize="10" fontFamily="Manrope">{data[data.length - 1]?.date}</text>
      <defs>
        <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C5A059" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#C5A059" stopOpacity="0"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/analytics/summary"),
        api.get("/admin/analytics/recent?limit=50"),
      ]);
      setSummary(s.data); setRecent(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !summary) {
    return <div className="text-white/50 py-10 text-center">Loading analytics…</div>;
  }

  const cards = [
    { key: "24h", label: "Last 24 hours" },
    { key: "7d",  label: "Last 7 days" },
    { key: "30d", label: "Last 30 days" },
    { key: "all", label: "All time" },
  ];

  return (
    <div data-testid="admin-analytics" className="space-y-10">
      <div>
        <div className="overline text-[#C5A059] mb-2">Analytics</div>
        <div className="font-display text-3xl">Site Traffic</div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.key} className="border border-white/10 p-6" data-testid={`analytics-card-${c.key}`}>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">{c.label}</div>
            <div className="font-display text-4xl mt-3">{summary[c.key]?.visits ?? 0}</div>
            <div className="text-xs text-white/50 mt-1">
              {summary[c.key]?.sessions ?? 0} unique {(summary[c.key]?.sessions ?? 0) === 1 ? "visitor" : "visitors"}
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="overline text-[#C5A059]">Daily Visits · 30 days</div>
          <button onClick={load} className="text-[10px] tracking-[0.24em] uppercase text-white/60 hover:text-[#C5A059]">Refresh</button>
        </div>
        <Chart data={summary.daily} />
      </div>

      {/* Top paths + recent */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-white/10">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="overline text-[#C5A059]">Top Pages · 30 days</div>
          </div>
          {(!summary.top_paths || summary.top_paths.length === 0) ? (
            <div className="p-6 text-white/40 text-sm text-center">No traffic yet.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {summary.top_paths.map((p) => (
                  <tr key={p.path} className="border-t border-white/5">
                    <td className="p-4 text-white/80 font-mono text-xs">{p.path}</td>
                    <td className="p-4 text-right text-white/60">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-white/10">
          <div className="px-6 py-4 border-b border-white/10">
            <div className="overline text-[#C5A059]">Recent Visits · Latest 50</div>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="p-6 text-white/40 text-sm text-center">No visits recorded yet.</div>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-t border-white/5" data-testid={`recent-visit-${r.id}`}>
                      <td className="p-3 text-white/50 whitespace-nowrap">
                        {new Date(r.ts).toLocaleString("en-GB", { hour12: false })}
                      </td>
                      <td className="p-3 text-white/80 font-mono truncate max-w-[220px]">{r.path}</td>
                      <td className="p-3 text-white/40 truncate max-w-[160px]">{r.session_id?.slice(0, 12)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
